/**
 * IANA timezone reference data shared by every scheduling/settings surface.
 *
 * `utc_offset` preserves the legacy display hint only. It is intentionally not used for
 * scheduling or date arithmetic: offsets change with daylight-saving rules (for example,
 * Europe/London is not UTC+00:00 for the whole year). Runtime scheduling must use `timezone`.
 *
 * The legacy service wrapped this constant in a fake one-second request and sorted the module
 * array in place on every call. v4 exposes an author-time ordered, frozen array synchronously.
 */
export interface TimezoneOption {
  readonly timezone: string
  readonly utc_offset: string
}

export const TIMEZONE_OPTIONS: readonly TimezoneOption[] = Object.freeze([
  { timezone: 'America/Los_Angeles', utc_offset: '-08:00' },
  { timezone: 'America/Vancouver', utc_offset: '-08:00' },
  { timezone: 'America/Denver', utc_offset: '-07:00' },
  { timezone: 'America/Chicago', utc_offset: '-06:00' },
  { timezone: 'America/Mexico_City', utc_offset: '-06:00' },
  { timezone: 'America/New_York', utc_offset: '-05:00' },
  { timezone: 'America/Toronto', utc_offset: '-05:00' },
  { timezone: 'America/Buenos_Aires', utc_offset: '-03:00' },
  { timezone: 'America/Sao_Paulo', utc_offset: '-03:00' },
  { timezone: 'Europe/Dublin', utc_offset: '+00:00' },
  { timezone: 'Europe/Lisbon', utc_offset: '+00:00' },
  { timezone: 'Europe/London', utc_offset: '+00:00' },
  { timezone: 'GMT', utc_offset: '+00:00' },
  { timezone: 'UTC', utc_offset: '+00:00' },
  { timezone: 'Africa/Lagos', utc_offset: '+01:00' },
  { timezone: 'Europe/Amsterdam', utc_offset: '+01:00' },
  { timezone: 'Europe/Berlin', utc_offset: '+01:00' },
  { timezone: 'Europe/Brussels', utc_offset: '+01:00' },
  { timezone: 'Europe/Madrid', utc_offset: '+01:00' },
  { timezone: 'Europe/Paris', utc_offset: '+01:00' },
  { timezone: 'Europe/Prague', utc_offset: '+01:00' },
  { timezone: 'Europe/Rome', utc_offset: '+01:00' },
  { timezone: 'Europe/Stockholm', utc_offset: '+01:00' },
  { timezone: 'Europe/Vienna', utc_offset: '+01:00' },
  { timezone: 'Europe/Warsaw', utc_offset: '+01:00' },
  { timezone: 'Europe/Zurich', utc_offset: '+01:00' },
  { timezone: 'Africa/Cairo', utc_offset: '+02:00' },
  { timezone: 'Africa/Johannesburg', utc_offset: '+02:00' },
  { timezone: 'Asia/Jerusalem', utc_offset: '+02:00' },
  { timezone: 'Europe/Athens', utc_offset: '+02:00' },
  { timezone: 'Africa/Nairobi', utc_offset: '+03:00' },
  { timezone: 'Asia/Riyadh', utc_offset: '+03:00' },
  { timezone: 'Europe/Istanbul', utc_offset: '+03:00' },
  { timezone: 'Europe/Moscow', utc_offset: '+03:00' },
  { timezone: 'Asia/Tehran', utc_offset: '+03:30' },
  { timezone: 'Asia/Dubai', utc_offset: '+04:00' },
  { timezone: 'Asia/Karachi', utc_offset: '+05:00' },
  { timezone: 'Asia/Kolkata', utc_offset: '+05:30' },
  { timezone: 'Asia/Dhaka', utc_offset: '+06:00' },
  { timezone: 'Asia/Bangkok', utc_offset: '+07:00' },
  { timezone: 'Asia/Ho_Chi_Minh', utc_offset: '+07:00' },
  { timezone: 'Asia/Jakarta', utc_offset: '+07:00' },
  { timezone: 'Asia/Hong_Kong', utc_offset: '+08:00' },
  { timezone: 'Asia/Kuala_Lumpur', utc_offset: '+08:00' },
  { timezone: 'Asia/Shanghai', utc_offset: '+08:00' },
  { timezone: 'Asia/Singapore', utc_offset: '+08:00' },
  { timezone: 'Asia/Taipei', utc_offset: '+08:00' },
  { timezone: 'Australia/Perth', utc_offset: '+08:00' },
  { timezone: 'Asia/Seoul', utc_offset: '+09:00' },
  { timezone: 'Asia/Tokyo', utc_offset: '+09:00' },
  { timezone: 'Australia/Brisbane', utc_offset: '+10:00' },
  { timezone: 'Australia/Melbourne', utc_offset: '+10:00' },
  { timezone: 'Australia/Sydney', utc_offset: '+10:00' },
  { timezone: 'Pacific/Auckland', utc_offset: '+12:00' },
])
